import { DocumentService } from '@/service/DocumentService';
import { UploadService } from '@/service/UploadService';
import { useAuthStore } from '@/store/storeAuth';
import { useNotificationStore } from '@/store/storeNotifications';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UploadDocument {
  id: string;
  name: string;
  src: string;
}

export interface PreviewState {
  isOpen: boolean;
  src: string;
  name: string;
  id: string;
  generatedText?: string;
  language?: string;
}

export const initialPreview: PreviewState = {
  isOpen: false,
  src: '',
  name: '',
  id: '',
  generatedText: '',
  language: '',
};

export interface UseUploadItemsOptions {
  onProcessedSuccess?: () => void;
}

export function useUploadItems(
  spaceId?: string,
  options?: UseUploadItemsOptions,
) {
  const { setNotification } = useNotificationStore(s => s);
  const { documents, setDocuments } = useAuthStore(s => s);
  const queryClient = useQueryClient();
  const onProcessedSuccessRef = useRef(options?.onProcessedSuccess);
  onProcessedSuccessRef.current = options?.onProcessedSuccess;

  const [spaceDocuments, setSpaceDocuments] = useState<UploadDocument[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<PreviewState>(initialPreview);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeDocId, setActiveDocId] = useState<string | null>(null);

  const docs: UploadDocument[] = spaceId ? spaceDocuments : documents ?? [];
  const activeDoc = docs.find(d => d.id === activeDocId) ?? null;

  const fetchDocuments = useCallback(async () => {
    try {
      if (spaceId) {
        const res = await DocumentService.getSpaceDocuments(spaceId);
        setSpaceDocuments(res?.data ?? []);
      } else {
        const res = await DocumentService.getDocuments();
        setDocuments(res?.data ?? []);
      }
    } catch {
      setNotification({
        type: 'error',
        message: 'Error fetching documents for space',
      });
    }
  }, [spaceId, setDocuments, setNotification]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    const onRefresh = () => fetchDocuments();
    window.addEventListener('sidebar-refresh', onRefresh);
    return () => window.removeEventListener('sidebar-refresh', onRefresh);
  }, [fetchDocuments]);

  const uploadFile = useCallback(
    async (file: File) => {
      try {
        setIsUploading(true);
        setNotification({ type: 'info', message: 'Uploading the File' });

        const formData = new FormData();
        formData.append('file', file);

        const uploadRes = await UploadService.uploadFile(formData);

        await DocumentService.createDocument({
          name: uploadRes.data.name,
          src: uploadRes.data.src,
          ...(spaceId !== undefined ? { spaceId } : {}),
        });

        setIsUploading(false);
        fetchDocuments();
        queryClient.invalidateQueries({ queryKey: ['spaceDocuments', spaceId] });
        queryClient.invalidateQueries({ queryKey: ['userDocuments'] });
        window.dispatchEvent(new CustomEvent('sidebar-refresh'));
        setNotification({
          type: 'success',
          message: 'File uploaded successfully',
        });
        onProcessedSuccessRef.current?.();
      } catch (error) {
        setIsUploading(false);
        const message =
          error instanceof Error ? error.message : 'Something went wrong';
        setNotification({
          type: 'error',
          message,
        });
      }
    },
    [fetchDocuments, setNotification, spaceId, queryClient],
  );

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    await uploadFile(file);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    try {
      await DocumentService.deleteOne(id, spaceId);
      closePopover();
      fetchDocuments();
      queryClient.invalidateQueries({ queryKey: ['spaceDocuments', spaceId] });
      queryClient.invalidateQueries({ queryKey: ['userDocuments'] });
      window.dispatchEvent(new CustomEvent('sidebar-refresh'));
    } catch {
      setNotification({ type: 'error', message: 'Error deleting document' });
    }
  };

  const handlePreview = (src: string, name: string, id: string) => {
    closePopover();
    if (src.includes('.txt') || src.includes('.csv')) {
      axios
        .get(src)
        .then(({ data }) => {
          setPreview({
            isOpen: true,
            src,
            name,
            id,
            generatedText: data.replace(/Page \d+: /g, '<br /><br />Page '),
          });
        })
        .catch(() => {
          setNotification({
            type: 'error',
            message: 'Error fetching document',
          });
        });
      return;
    }
    setPreview({ isOpen: true, src, name, id });
  };

  const handleDownload = (src: string, name: string) => {
    closePopover();
    axios.get(src, { responseType: 'blob' }).then(res => {
      const url = URL.createObjectURL(res.data);
      Object.assign(document.createElement('a'), {
        href: url,
        download: name,
      }).click();
      URL.revokeObjectURL(url);
    });
  };

  const closePopover = () => {
    setAnchorEl(null);
    setActiveDocId(null);
  };

  const openPopover = (anchor: HTMLElement, docId: string) => {
    setAnchorEl(anchor);
    setActiveDocId(docId);
  };

  return {
    docs,
    activeDoc,
    isUploading,
    preview,
    anchorEl,
    setPreview,
    uploadFile,
    handleUpload,
    handleDelete,
    handlePreview,
    handleDownload,
    closePopover,
    openPopover,
  };
}
